import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import { ArrowDown2 } from 'iconsax-react'

import { useAreYouSure } from '../../../../providers/AreYouSureProvider'
import { fetchInboxes } from '../../../../slices/inboxes'
import { getInbox } from '../IntegrationModal/helpers'
import {
  selectExternalChannel,
  updateExternalChannels,
  failed
} from '../../../../slices/externalChannel'
import { getFormatted } from '../../../../utilities/phoneNumber'
import { AddSuccessAlert } from '../../../../slices/alert'

import UserSearch from '../../../../pages/Tasks/Tabs/AllTasksTab/Filters/UserSearch'
import Dropdown from '../../../atoms/Dropdown'
import ModalTextField from '../../../atoms/ModalTextField'
import ErrorToast from '../../../molecules/ErrorToast'
import BaseModal from '../BaseModal2'
import EndControls from '../EndControls'
import Section from '../IntegrationModal/Section'
import PhoneFlag from '../../../atoms/PhoneFlag'

import useStyles from './styles'

const ExternalChannelsModal = ({ open, onClose, data }) => {
  const dispatch = useAppDispatch()
  const [values, setValues] = React.useState(null)
  const [changed, setChanged] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const { setAction, setReason, setV2 } = useAreYouSure()
  const {
    loaded,
    loading,
    inboxesValues: inboxes
  } = useAppSelector(storeState => storeState.inboxes)
  const { error } = useAppSelector(selectExternalChannel)

  const classes = useStyles()
  React.useEffect(() => {
    setValues(data)
  }, [data])

  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchInboxes())
    }
  }, [loaded, loading, dispatch])

  const handleSubmit = React.useCallback(async () => {
    if (!changed) {
      onClose()
      return
    }
    setSaving(true)

    try {
      const data = { ...values }
      delete data.id
      delete data.created_at
      delete data.updated_at
      await dispatch(
        updateExternalChannels(values?.id, {
          type: values?.type,
          value: values?.value,
          default_inbox_id: values?.default_inbox_id,
          associated_user_id:
            values?.associated_user_id === 'null'
              ? null
              : values?.associated_user_id
        })
      )
      onClose()
      dispatch(AddSuccessAlert('Changes saved'))
    } catch (error) {
      dispatch(failed(error))
      setSaving(false)
    }
  }, [changed, setSaving, onClose, dispatch, values])

  const formattedValue =
    values?.type !== 'email' ? getFormatted(values?.value) : values?.value

  return (
    <>
      <BaseModal
        open={open}
        showTitleOnMobile={true}
        onClose={() => {
          const action = onClose

          if (changed) {
            setReason('You have unsaved changes')
            setAction(action)
            setV2(true)
          } else {
            action()
          }
        }}
        title={`Edit External Channel`}
        PaperProps={{ style: { width: '50%' } }}
      >
        <ModalTextField title='Type' className={classes.input}>
          <label> {values?.type}</label>
        </ModalTextField>

        <ModalTextField title='Value' className={classes.input}>
          {values?.type === 'email' ? (
            <label> {formattedValue}</label>
          ) : (
            <label>
              <PhoneFlag
                style={{ marginRight: '5px' }}
                value={formattedValue}
              />
              {formattedValue}
            </label>
          )}
        </ModalTextField>

        <Section name='Inbox'>
          <div className={classes.dropdown}>
            <div>Default Inbox</div>

            <Dropdown
              options={inboxes.map(t => t.id)}
              renderOption={key => getInbox(inboxes, key)}
              selected={values?.default_inbox_id}
              onSelect={val => {
                setValues({ ...values, default_inbox_id: val })
                setChanged(true)
              }}
              hideAdd
              hideClose
              stringOnly
              disablePortal
            >
              <div className='menu'>
                <div>{getInbox(inboxes, values?.default_inbox_id)}</div>

                <ArrowDown2 />
              </div>
            </Dropdown>
          </div>
          <small className={classes.info}>
            The default inbox all inquiries on this channel will go into. Every
            channel must have an inbox to be able to process incoming
            communications.
          </small>
          {values?.default_inbox_id === '' && (
            <small className={classes.warning}>Default inbox is required</small>
          )}
        </Section>

        <Section name='Associated User'>
          <div className={classes.userSection}>
            <div>User</div>

            <UserSearch
              hideMe={false}
              value={values?.associated_user_id}
              setValue={val => {
                setValues({ ...values, associated_user_id: val })
                setChanged(true)
              }}
              withBorder
              switchIcon
            />
          </div>
          <small className={classes.info}>
            For associating a channel with a specific user. If a number has no
            user, then it is considered global.
            <strong> Not yet supported.</strong>
          </small>
        </Section>

        <EndControls
          disabled={values?.default_inbox_id === ''}
          onSubmit={handleSubmit}
          submitText='SAVE'
          showExtraIcons={false}
          SubmitIcon={() => null}
          saving={saving}
        />
      </BaseModal>

      <ErrorToast error={error} autoHideDuration={2000} />
    </>
  )
}

export default ExternalChannelsModal
